/**
 * Mention identity resolution.
 *
 * The API stores mentions inside the body text, so the label has to be matched
 * once against the people known for that post/comment/thread. The result of
 * that match is a user GUID kept on the rendered mention, and navigation uses
 * the GUID — never a second lookup against the visible label, which would
 * break as soon as someone is renamed.
 *
 * A label may be a username or a display/first name. First names are not
 * unique, so a label claimed by two different people resolves to "ambiguous"
 * and the UI declines to guess.
 */
import { getFirstName } from './postDisplay';

export interface MentionCandidate {
  /** Unique identity. Candidates without one cannot be navigated to. */
  userGuid?: string | null;
  username?: string | null;
  firstName?: string | null;
  /** Full or display name; its first token is indexed too, for "@FirstName". */
  displayName?: string | null;
}

export interface MentionMatch {
  /** Resolved unique id, or null when unknown or ambiguous. */
  userGuid: string | null;
  /** True when more than one person answers to this label. */
  ambiguous: boolean;
  /**
   * First name of the resolved person, for display as `@FirstName`. Undefined
   * when the match is ambiguous or unresolved, or when no first name is known.
   */
  displayName?: string;
}

export type MentionIndex = ReadonlyMap<string, MentionMatch>;

export const UNRESOLVED_MENTION: MentionMatch = { userGuid: null, ambiguous: false, displayName: undefined };

/** Mentions are written without spaces, so labels compare on a bare token. */
export const normalizeMentionLabel = (label: string): string =>
  label.replace(/^@+/, '').trim().toLowerCase();

/**
 * A written mention cannot contain spaces, so a multi-word name is reachable
 * only in its compacted form ("Emma Watson" → "EmmaWatson"). The composer
 * writes that form when a first name alone would be ambiguous.
 */
export const compactMentionLabel = (label: string | null | undefined): string =>
  (label ?? '').replace(/\s+/g, '');

/**
 * The label reduced to what a written mention can hold: the parser reads
 * `@[a-zA-Z0-9_.]+`, so anything else in a name is dropped ("O'Neil (coach)" →
 * "ONeilcoach"). The composer writes this form and the index answers to it.
 */
export const toMentionToken = (label: string | null | undefined): string =>
  compactMentionLabel(label).replace(/[^a-zA-Z0-9_.]/g, '');

const addLabel = (
  index: Map<string, MentionMatch>,
  label: string | null | undefined,
  userGuid: string,
  displayName?: string,
): void => {
  const key = normalizeMentionLabel(label ?? '');
  if (!key) return;

  const existing = index.get(key);
  if (!existing) {
    index.set(key, { userGuid, ambiguous: false, displayName });
    return;
  }
  if (existing.userGuid && existing.userGuid.toLowerCase() === userGuid.toLowerCase()) {
    return;
  }
  // Two different people answer to this label (e.g. two members named Emma).
  index.set(key, { userGuid: null, ambiguous: true, displayName: undefined });
};

/**
 * Builds the label → identity index for one rendering context (post author,
 * tagged buddies, comment authors, thread participants, the current user).
 */
export function buildMentionIndex(
  candidates: readonly (MentionCandidate | null | undefined)[],
): MentionIndex {
  const index = new Map<string, MentionMatch>();

  for (const candidate of candidates) {
    const guid = candidate?.userGuid?.trim();
    if (!guid) continue;

    // The display name carried on the match: first name when the API sends
    // it, otherwise the first word of the display name, otherwise the handle.
    const displayName =
      candidate?.firstName?.trim() ||
      getFirstName(candidate?.displayName) ||
      candidate?.username?.trim() ||
      undefined;

    // Each label is indexed as written and in the form a mention can be
    // written in, so "@EmmaWatson" finds the member stored as "Emma Watson".
    const add = (label: string | null | undefined): void => {
      addLabel(index, label, guid, displayName);
      const token = toMentionToken(label);
      if (token && token !== label) addLabel(index, token, guid, displayName);
    };

    add(candidate?.username);
    add(candidate?.firstName);
    add(candidate?.displayName);

    // "@Emma" for a display name of "Emma Watson".
    add(getFirstName(candidate?.displayName));
  }

  return index;
}

/** Looks up a written label. Unknown labels resolve to an unresolved match. */
export function resolveMention(index: MentionIndex, label: string): MentionMatch {
  return index.get(normalizeMentionLabel(label)) ?? UNRESOLVED_MENTION;
}
